import {
  searchCategoryWithTransaction,
  searchLocationWithTransaction,
  searchStaffWithTransaction,
} from "apis";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterSingleSelect,
  FilterTimeRange,
} from "components/commons";
import { useApi, useMount } from "hooks";
import {
  locationWithTransactionFilterResponse,
  locationWithTransactionFilterRequest,
  staffWithTransactionFilterRequest,
  staffWithTransactionFilterResponse,
  categoryWithTransactionFilterResponse,
  categoryWithTransactionFilterRequest,
} from "mappers";
import React, { useCallback, useEffect, useMemo } from "react";
import { mapObject, mapObjectsToSelect } from "services";
import lang from "translations";

const CategoryReportFilter = ({
  filterState,
  requestState,
  modifyFilter,
  clearFilter,
  onClear,
  onApply,
}) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchLocationWithTransaction,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
  });

  const {
    request: searchStaffRequest,
    loading: loadingStaff,
    mappedData: staffs,
  } = useApi({
    api: searchStaffWithTransaction,
    isArray: true,
    mapper: staffWithTransactionFilterResponse,
  });

  const {
    request: searchCategoryRequest,
    loading: loadingCategory,
    mappedData: categories,
  } = useApi({
    api: searchCategoryWithTransaction,
    isArray: true,
    mapper: categoryWithTransactionFilterResponse,
  });

  useMount(() => {
    fetchLocations(requestState);
    fetchStaffs(requestState);
    fetchCategories(requestState);
  });

  const fetchLocations = useCallback(
    (requestState) => {
      searchLocationRequest(mapObject({ ...requestState }, locationWithTransactionFilterRequest));
    },
    [searchLocationRequest]
  );

  const fetchStaffs = useCallback(
    (requestState) => {
      searchStaffRequest(mapObject({ ...requestState }, staffWithTransactionFilterRequest));
    },
    [searchStaffRequest]
  );

  const fetchCategories = useCallback(
    (requestState) => {
      searchCategoryRequest(mapObject({ ...requestState }, categoryWithTransactionFilterRequest));
    },
    [searchCategoryRequest]
  );

  const applyFilterCb = useCallback(
    async (searchKey) => {
      await onApply(searchKey);
    },
    [onApply]
  );

  const clearFilterCb = useCallback(() => {
    const { filterState, requestState } = clearFilter();
    onClear(filterState, requestState);
    fetchLocations(requestState);
    fetchStaffs(requestState);
    fetchCategories(requestState);
  }, [clearFilter, onClear, fetchLocations, fetchStaffs, fetchCategories]);

  const changeDateRangeCb = useCallback(
    (value) => {
      const { requestState } = modifyFilter("dateRange", { value });
      fetchLocations(requestState);
      fetchStaffs(requestState);
      fetchCategories(requestState);
    },
    [fetchLocations, fetchStaffs, fetchCategories, modifyFilter]
  );

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" });
  }, [locations]);

  useEffect(() => {
    if (!locationOptions.length && filterState.locations.length) {
      modifyFilter("locations", { value: [] });
    }
  }, [locationOptions, filterState.locations, modifyFilter]);

  const staffOptions = useMemo(() => {
    let sorted = staffs
      .map((staff) => {
        staff.name = staff.name.trim();
        return staff;
      })
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    return mapObjectsToSelect(sorted, { textKey: "name" });
  }, [staffs]);

  const categoryOptions = useMemo(() => {
    let sorted = categories
      .map((category) => {
        category.name = category.name.trim();
        return category;
      })
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    return [
      { text: lang.allCategories, value: null },
      ...mapObjectsToSelect(sorted, { textKey: "name" }),
    ];
  }, [categories]);

  return (
    <FilterBar
      className="my-lg"
      filterState={filterState}
      searchBox={false}
      onClear={clearFilterCb}
      onApply={applyFilterCb}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dateRange}
        maxDays={90}
        value={filterState.dateRange}
        onChange={changeDateRangeCb}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.timeRange}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterMultiSelect
        id="locations"
        label={lang.outlet}
        allLabel={lang.allLocations}
        emptyLabel={lang.noLocationAvailable}
        disableWhenEmpty={false}
        loading={loadingLocation}
        options={locationOptions}
        value={filterState.locations}
        onChange={(value) => modifyFilter("locations", { value })}
      />
      <FilterMultiSelect
        id="staffs"
        label={lang.staff}
        allLabel={lang.allStaff}
        emptyLabel={lang.noAvailableStaff}
        disableWhenEmpty={false}
        loading={loadingStaff}
        options={staffOptions}
        value={filterState.staffs}
        onChange={(value) => modifyFilter("staffs", { value })}
      />
      <FilterSingleSelect
        id="category"
        label={lang.category}
        loading={loadingCategory}
        emptyLabel={lang.noAvailableCategories}
        summaryFallback={lang.allCategories}
        options={categoryOptions}
        value={filterState.category}
        onChange={(value) => modifyFilter("category", { value })}
      />
    </FilterBar>
  );
};

export default CategoryReportFilter;
